<template>
  <div class="permissions-container">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          Permissions
        </h1>
        <v-btn
          v-if="hasPermission('write:permission')"
          color="primary"
          class="mank-btn"
          elevation="0"
          @click="openCreateDialog">
          <Icon name="plus" variant="solid" color="onPrimary"/>
          Create
        </v-btn>
      </div>

      <div class="main-content">
        <div class="table-area table-area--pad">
          <div v-if="loading" class="d-flex justify-center py-12">
            <v-progress-circular indeterminate color="primary"/>
          </div>

          <div v-else-if="!permissions.length" class="empty-state text-center">
            <div class="empty-state-icon mb-4">
              <v-icon size="80" color="grey-lighten-2">
                mdi-shield-key-outline
              </v-icon>
            </div>
            <div class="empty-state-text mb-4">
              <h3 class="ds-panel-title mb-1">
                No Permissions Found
              </h3>
              <p class="text-body-3 text--secondary">
                Create a new permission by clicking the button above.
              </p>
            </div>
          </div>

          <v-data-table
            v-else
            :headers="headers"
            :items="permissions"
            :loading="loading"
            class="mank-data-table"
            :items-per-page="-1"
            hide-default-footer
            no-data-text="No permissions found"
            loading-text="Loading...">
            <template v-slot:item.name="{ item }">
              <span class="text-element-2 font-weight-medium">{{ item.name }}</span>
            </template>
            <template v-slot:item.description="{ item }">
              <span class="text-element-2 text--secondary">{{ item.description || '—' }}</span>
            </template>
          </v-data-table>
        </div>
      </div>
    </div>

    <!-- Create Dialog -->
    <v-dialog v-model="createDialog" max-width="480" persistent>
      <v-card class="mank-dialog">
        <div class="dialog-title">
          Create Permission
        </div>
        <div class="dialog-body">
          <div class="form-field">
            <label class="field-label">Name</label>
            <MankTextField
              v-model="form.name"
              placeholder="e.g. agents:read"
              hide-details="auto"
              :error-messages="errors.name"/>
          </div>
          <div class="form-field">
            <label class="field-label">Description</label>
            <MankTextarea
              v-model="form.description"
              placeholder="Describe this permission"
              hide-details="auto"
              rows="3"/>
          </div>
        </div>
        <div class="dialog-actions">
          <v-btn text class="mank-btn mank-btn--text" @click="closeCreateDialog">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            class="mank-btn"
            elevation="0"
            :loading="saving"
            @click="createPermission">
            Create
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import PermissionService from '../services/PermissionService';
import Icon from '../components/Icon.vue';
import MankTextField from '../components/MankTextField.vue';
import MankTextarea from '../components/MankTextarea.vue';

export default {
  name: 'Permissions',
  components: { Icon, MankTextField, MankTextarea },
  data() {
    return {
      permissions: [],
      loading: false,
      saving: false,
      createDialog: false,
      form: { name: '', description: '' },
      errors: { name: '' },
      headers: [
        { text: 'NAME', value: 'name', sortable: true },
        { text: 'DESCRIPTION', value: 'description', sortable: false },
      ],
    };
  },
  computed: { ...mapGetters('auth', ['isSuperuser', 'hasPermission']) },
  mounted() {
    this.fetchPermissions();
  },
  methods: {
    async fetchPermissions() {
      if (!this.$store.getters['auth/isAuthenticated']) return;
      this.loading = true;
      try {
        this.permissions = await PermissionService.getAll();
      } catch (e) {
        this.$store.dispatch('ui/showNotification', { type: 'error', message: 'Failed to load permissions.' });
      } finally {
        this.loading = false;
      }
    },
    openCreateDialog() {
      this.form = { name: '', description: '' };
      this.errors = { name: '' };
      this.createDialog = true;
    },
    closeCreateDialog() {
      this.createDialog = false;
    },
    async createPermission() {
      this.errors.name = '';
      if (!this.form.name.trim()) {
        this.errors.name = 'Name is required.';
        return;
      }
      this.saving = true;
      try {
        await PermissionService.createPermission(this.form);
        this.$store.dispatch('ui/showNotification', { type: 'success', message: 'Permission created.' });
        this.closeCreateDialog();
        this.fetchPermissions();
      } catch (e) {
        this.$store.dispatch('ui/showNotification', { type: 'error', message: 'Failed to create permission.' });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.permissions-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content-area {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 32px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: $light-foreground-1;
}

.main-content {
  flex: 1;
}

.table-area {
  background: $light-surface-1;
  border-radius: 12px;
  border: 1px solid $light-border;

  &--pad {
    padding: 24px;
  }
}

.empty-state {
  padding: 60px 0;
}

.dialog-title {
  font-size: 18px;
  font-weight: 700;
  padding: 24px 24px 16px;
}

.dialog-body {
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  color: $light-foreground-1;
  font-weight: 600;
  font-size: $body-3-size;
  line-height: 1.2;
}

.dialog-actions {
  padding: 16px 24px 24px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
</style>
